/**
 * Single Settings page — every category lives at the same level in a
 * flat menu. No tabs, no sub-tabs, no nested sub-navigation.
 *
 * Tier-direkter Render:
 *   - trial / free / pro                              → simplified menu
 *     (Sprach-Agent + Kalender + … ; LLM/Voice/Filler ausgeblendet)
 *   - enterprise / ultimate / admin / admin-target    → vollständige Tabs
 *     (LLM/Voice/Filler eingeblendet, Agent-Picker entfällt)
 *
 * Es gibt keinen Pro/Enterprise-Toggle und keinen "Sorglos-Anfänger"-
 * Modus mehr — die Sicht folgt strikt der aktuellen Mitgliedschaft.
 */
import { Loader2, Save } from 'lucide-react';
import * as React from 'react';
import { NavLink, Outlet, useLocation, useParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { cn } from '../../lib/utils';
import { PhoneSettingsProvider, usePhoneSettingsForm } from './phone/PhoneSettingsContext';
import { AdminContextBanner } from '../../components/AdminContextBanner';
import { useUser } from '../../auth/AuthProvider';

interface MenuItem {
  /** URL path relative to the settings root (no leading slash). */
  slug: string;
  label: string;
}

const MENU: ReadonlyArray<MenuItem> = [
  { slug: 'provider', label: 'Anbieter & Nummer' },
  { slug: 'agent', label: 'Sprach-Agent' },
  { slug: 'llm', label: 'KI-Modell' },
  { slug: 'voice', label: 'Stimme & Sprache' },
  { slug: 'calendar', label: 'Kalender' },
  { slug: 'filler', label: 'Latenz & Filler' },
  { slug: 'apikeys', label: 'API-Schlüssel' },
  { slug: 'event-types', label: 'Termin-Typen' },
  { slug: 'transfer', label: 'Weiterleitung' },
  { slug: 'calls', label: 'Anruf-Log' },
  { slug: 'tickets', label: 'Tickets' },
  { slug: 'mail-templates', label: 'Vorlagen' },
  { slug: 'test', label: 'Testen' },
];

/**
 * Tabs we hide from non-enterprise users. They live in the simplified
 * "Sprach-Agent" tab now: ein Pro-User wählt einen vorkonfigurierten
 * Agenten und sieht nur die drei kunden-orientierten Felder
 * (Begrüßung, Verabschiedung, Wissensbasis).
 */
const ENTERPRISE_ONLY_SLUGS: ReadonlySet<string> = new Set([
  'llm',
  'voice',
  'filler',
]);

/**
 * Tabs nur für Pro-User. Der Agent-Picker ist für Enterprise-User
 * irrelevant, weil sie ohnehin alles per Hand konfigurieren.
 */
const PRO_ONLY_SLUGS: ReadonlySet<string> = new Set(['agent']);

/**
 * URL-Präfix abhängig vom Routing-Kontext:
 *   - `/settings`                      (Self-Edit)
 *   - `/admin/users/:uid/settings`     (Admin-Target)
 */
function useSettingsBasePath(): string {
  const { uid } = useParams<{ uid?: string }>();
  return uid ? `/admin/users/${uid}/settings` : '/settings';
}

export function SettingsLayout() {
  return (
    <PhoneSettingsProvider>
      <SettingsShell />
    </PhoneSettingsProvider>
  );
}

function SettingsShell() {
  const base = useSettingsBasePath();
  const { uid } = useParams<{ uid?: string }>();
  const { isAdmin, profile } = useUser();
  const isAdminTarget = !!uid;
  const myTier = profile?.membership?.tier ?? null;

  // API-Keys: nur Admins, Admin-Targeting, oder Ultimate-Kunden.
  const canSeeApiKeys = isAdmin || isAdminTarget || myTier === 'ultimate';

  // Tier-basiertes Split:
  // Enterprise/Ultimate/Admin/Admin-Target → volle technische Tabs.
  // Alle anderen (trial/free/pro)          → simplified Agent-Picker.
  const isEnterpriseScope =
    isAdmin || isAdminTarget || myTier === 'enterprise' || myTier === 'ultimate';

  const items = React.useMemo(
    () =>
      MENU.filter((m) => {
        if (m.slug === 'apikeys' && !canSeeApiKeys) return false;
        if (isEnterpriseScope && PRO_ONLY_SLUGS.has(m.slug)) return false;
        if (!isEnterpriseScope && ENTERPRISE_ONLY_SLUGS.has(m.slug)) return false;
        return true;
      }).map((m) => ({
        ...m,
        to: `${base}/${m.slug}`,
      })),
    [base, canSeeApiKeys, isEnterpriseScope],
  );

  return (
    <div className="grid gap-6 lg:grid-cols-[220px_1fr]">
      <aside className="lg:sticky lg:top-20 lg:self-start">
        <nav
          aria-label="Einstellungs-Bereiche"
          className="-mx-1 flex gap-1 overflow-x-auto pb-1 lg:hidden"
        >
          {items.map((m) => (
            <MenuPill key={m.to} item={m} basePath={base} />
          ))}
        </nav>
        <nav
          aria-label="Einstellungs-Bereiche"
          className="hidden flex-col gap-0.5 lg:flex"
        >
          {items.map((m) => (
            <MenuRow key={m.to} item={m} basePath={base} />
          ))}
        </nav>
      </aside>
      <div className="min-w-0 space-y-4">
        <AdminContextBanner />
        <SaveBar />
        <Outlet />
      </div>
    </div>
  );
}

interface ResolvedMenuItem extends MenuItem {
  to: string;
}

function MenuRow({ item, basePath }: { item: ResolvedMenuItem; basePath: string }) {
  const { pathname } = useLocation();
  const callsBase = `${basePath}/calls`;
  const isActive =
    item.slug === 'calls'
      ? pathname === callsBase || pathname.startsWith(`${callsBase}/`)
      : pathname === item.to;
  return (
    <NavLink
      to={item.to}
      className={cn(
        'rounded-md px-3 py-2 text-sm font-medium transition-colors',
        isActive
          ? 'bg-secondary text-foreground'
          : 'text-muted-foreground hover:bg-secondary/50 hover:text-foreground',
      )}
    >
      {item.label}
    </NavLink>
  );
}

function MenuPill({ item, basePath }: { item: ResolvedMenuItem; basePath: string }) {
  const { pathname } = useLocation();
  const callsBase = `${basePath}/calls`;
  const isActive =
    item.slug === 'calls'
      ? pathname === callsBase || pathname.startsWith(`${callsBase}/`)
      : pathname === item.to;
  return (
    <NavLink
      to={item.to}
      className={cn(
        'shrink-0 rounded-md border px-3 py-1.5 text-sm font-medium',
        isActive
          ? 'border-border bg-secondary text-foreground'
          : 'border-transparent text-muted-foreground',
      )}
    >
      {item.label}
    </NavLink>
  );
}

function SaveBar() {
  const { dirty, saving, save } = usePhoneSettingsForm();
  if (!dirty) return null;
  return (
    <div className="flex items-center justify-between rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm">
      <span className="text-amber-200">Du hast ungespeicherte Telefon-Einstellungen.</span>
      <Button onClick={() => void save()} size="sm" disabled={saving}>
        {saving ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-2 h-3.5 w-3.5" />}
        Speichern
      </Button>
    </div>
  );
}
